/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Grid,
  TextField,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
  FileCopy as DuplicateIcon,
  Timeline as TimelineIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { indicatorSetApi, indicatorApi } from '../../services/api';
import { formatStoredDateTime } from '../../utils/datetime';
import { IndicatorConfigurator } from '../common/IndicatorConfigurator';

const IndicatorSetDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // State
  const [isEditMode, setIsEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    category: '',
    tags: [] as string[],
    indicators: [] as any[],
  });
  const [currentIndicator, setCurrentIndicator] = useState<any>(null);
  const [newTag, setNewTag] = useState('');

  // Validate ID
  const indicatorSetId = id ? parseInt(id, 10) : NaN;
  const isValidId = !isNaN(indicatorSetId);

  // Fetch indicator set
  const { data: indicatorSet, isLoading, error } = useQuery({
    queryKey: ['indicator-set', indicatorSetId],
    queryFn: () => indicatorSetApi.get(indicatorSetId),
    enabled: isValidId,
  });

  // Fetch available indicators
  const { data: availableIndicators = {} } = useQuery<Record<string, any>>({
    queryKey: ['available-indicators'],
    queryFn: indicatorApi.getAvailable,
  });

  // Load data into form when entering edit mode or data loads
  useEffect(() => {
    if (indicatorSet && isEditMode) {
      setEditFormData({
        name: indicatorSet.name,
        description: indicatorSet.description || '',
        category: indicatorSet.category || '',
        tags: indicatorSet.tags || [],
        indicators: indicatorSet.indicators || [],
      });
    }
  }, [indicatorSet, isEditMode]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: any) => indicatorSetApi.update(indicatorSetId, data),
    onSuccess: () => {
      toast.success('Indicator set updated successfully');
      queryClient.invalidateQueries({ queryKey: ['indicator-set', indicatorSetId] });
      queryClient.invalidateQueries({ queryKey: ['indicator-sets'] });
      setIsEditMode(false);
    },
    onError: (error: any) => {
      const errorMsg = error.response?.data?.detail || 'Failed to update indicator set';
      toast.error(errorMsg);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => indicatorSetApi.delete(indicatorSetId),
    onSuccess: () => {
      toast.success('Indicator set deleted successfully');
      navigate('/ai/indicator-sets');
    },
    onError: () => {
      toast.error('Failed to delete indicator set');
    },
  });

  // Duplicate mutation
  const duplicateMutation = useMutation({
    mutationFn: () => indicatorSetApi.duplicate(indicatorSetId),
    onSuccess: (newIndicatorSet) => {
      toast.success('Indicator set duplicated successfully');
      queryClient.invalidateQueries({ queryKey: ['indicator-sets'] });
      navigate(`/ai/indicator-sets/${newIndicatorSet.id}`);
    },
    onError: () => {
      toast.error('Failed to duplicate indicator set');
    },
  });

  // Handlers
  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setCurrentIndicator(null);
    setNewTag('');
  };

  const handleSave = () => {
    if (!editFormData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (editFormData.indicators.length === 0) {
      toast.error('At least one indicator is required');
      return;
    }
    updateMutation.mutate(editFormData);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this indicator set?')) {
      deleteMutation.mutate();
    }
  };

  const handleDuplicate = () => {
    duplicateMutation.mutate();
  };

  const handleAddIndicator = () => {
    if (!currentIndicator || !currentIndicator.indicator_name?.trim()) {
      toast.error('Please configure an indicator');
      return;
    }
    setEditFormData({
      ...editFormData,
      indicators: [...editFormData.indicators, { ...currentIndicator }],
    });
    setCurrentIndicator(null);
  };

  const handleRemoveIndicator = (index: number) => {
    setEditFormData({
      ...editFormData,
      indicators: editFormData.indicators.filter((_, i) => i !== index),
    });
  };

  const handleAddTag = () => {
    if (newTag.trim() && !editFormData.tags.includes(newTag.trim())) {
      setEditFormData({
        ...editFormData,
        tags: [...editFormData.tags, newTag.trim()],
      });
      setNewTag('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setEditFormData({
      ...editFormData,
      tags: editFormData.tags.filter((t) => t !== tag),
    });
  };

  // Check for invalid ID
  if (!isValidId) {
    return (
      <Alert severity="error">
        Invalid indicator set ID. Please check the URL.
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !indicatorSet) {
    return (
      <Alert severity="error">
        Failed to load indicator set. Please try again.
      </Alert>
    );
  }

  // Display data - use editFormData in edit mode, indicatorSet in view mode
  const displayData = isEditMode ? editFormData : indicatorSet;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button startIcon={<BackIcon />} onClick={() => navigate('/ai/indicator-sets')}>
            Back
          </Button>
          <TimelineIcon fontSize="large" color="primary" />
          <Typography variant="h4">{indicatorSet.name}</Typography>
        </Box>
        <Box display="flex" gap={1}>
          {isEditMode ? (
            <>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancelEdit}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={updateMutation.isPending}
              >
                Save Changes
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                startIcon={<DuplicateIcon />}
                onClick={handleDuplicate}
                disabled={duplicateMutation.isPending}
              >
                Duplicate
              </Button>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={handleEdit}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
              >
                Delete
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Overview Card */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Overview
              </Typography>
              <Divider sx={{ my: 2 }} />

              {/* Name */}
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Name {isEditMode && '*'}
                </Typography>
                {isEditMode ? (
                  <TextField
                    fullWidth
                    size="small"
                    value={editFormData.name}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, name: e.target.value })
                    }
                    required
                  />
                ) : (
                  <Typography variant="body1">{displayData.name}</Typography>
                )}
              </Box>

              {/* Description */}
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Description
                </Typography>
                {isEditMode ? (
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    value={editFormData.description}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, description: e.target.value })
                    }
                  />
                ) : (
                  <Typography variant="body1">
                    {displayData.description || 'No description'}
                  </Typography>
                )}
              </Box>

              {/* Category */}
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Category
                </Typography>
                {isEditMode ? (
                  <TextField
                    fullWidth
                    size="small"
                    value={editFormData.category}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, category: e.target.value })
                    }
                  />
                ) : displayData.category ? (
                  <Chip label={displayData.category} size="small" />
                ) : (
                  <Typography variant="body1">-</Typography>
                )}
              </Box>

              {/* Tags */}
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Tags
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5} mb={isEditMode ? 1 : 0}>
                  {displayData.tags.length > 0 ? (
                    displayData.tags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        variant="outlined"
                        onDelete={isEditMode ? () => handleRemoveTag(tag) : undefined}
                      />
                    ))
                  ) : (
                    <Typography variant="body2">No tags</Typography>
                  )}
                </Box>
                {isEditMode && (
                  <Box display="flex" gap={1}>
                    <TextField
                      size="small"
                      label="Add Tag"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddTag();
                        }
                      }}
                    />
                    <Button onClick={handleAddTag} variant="outlined" size="small">
                      Add
                    </Button>
                  </Box>
                )}
              </Box>

              {!isEditMode && (
                <>
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">
                      Indicator Count
                    </Typography>
                    <Typography variant="body1">
                      {indicatorSet.indicator_count || 0}
                    </Typography>
                  </Box>

                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">
                      Usage Count
                    </Typography>
                    <Typography variant="body1">
                      {indicatorSet.usage_count || 0}
                    </Typography>
                  </Box>

                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip
                      label={indicatorSet.is_active ? 'Active' : 'Inactive'}
                      color={indicatorSet.is_active ? 'success' : 'default'}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Created
                    </Typography>
                    <Typography variant="body2">
                      {formatStoredDateTime(indicatorSet.created_at, undefined, '-')}
                    </Typography>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Indicators Table */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Indicators Configuration {isEditMode && '*'}
            </Typography>
            <Divider sx={{ my: 2 }} />

            {displayData.indicators.length === 0 ? (
              <Alert severity="info">
                {isEditMode ? 'Add at least one indicator' : 'No indicators configured'}
              </Alert>
            ) : (
              <>
                {isEditMode ? (
                  /* Edit Mode - Show editable list */
                  <Box>
                    {editFormData.indicators.map((indicator, index) => (
                      <Paper key={index} sx={{ p: 2, mb: 1, bgcolor: isDark ? 'grey.900' : 'grey.50' }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="body1" fontWeight="bold">
                              {indicator.indicator_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Params: {JSON.stringify(indicator.params)}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveIndicator(index)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                ) : (
                  /* View Mode - Show table */
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Indicator Name</TableCell>
                          <TableCell>Parameters</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {indicatorSet.indicators.map((indicator, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Chip
                                label={indicator.indicator_name}
                                color="primary"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ maxWidth: 500 }}>
                                <pre style={{ margin: 0, fontSize: '0.75rem' }}>
                                  {JSON.stringify(indicator.params, null, 2)}
                                </pre>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}

            {/* Add Indicator Section (Edit Mode Only) */}
            {isEditMode && (
              <Paper sx={{ p: 2, mt: 2, bgcolor: isDark ? 'grey.800' : 'grey.100' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Add Indicator
                </Typography>
                <IndicatorConfigurator
                  availableIndicators={availableIndicators}
                  value={currentIndicator}
                  onChange={setCurrentIndicator}
                  showOutputInfo={true}
                />
                <Button
                  onClick={handleAddIndicator}
                  variant="contained"
                  startIcon={<AddIcon />}
                  size="small"
                  sx={{ mt: 2 }}
                  disabled={!currentIndicator?.indicator_name}
                >
                  Add Indicator
                </Button>
              </Paper>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default IndicatorSetDetail;
